import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { ChevronRightIcon, SlashIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import Link from "@tiptap/extension-link";
import Typography from "@tiptap/extension-typography";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link as NavLink, useNavigate } from "react-router-dom";
import {
  ProjectProgressComparisonData,
  ProjectProgressComparisonSort,
  useGetProjectProgressComparison,
} from "src/api/Analytics/project";
import { ParentStatus } from "src/api/General/status-groups";
import { ProjectsV2FilterGroup } from "src/api/Services/Projects/projects";
import { InternalPhaseUpdateData } from "src/api/Services/SharedSpace/phases";
import { useGetJourneyTemplates } from "src/api/Services/Tasks/journeys";
import { useGetTaskersForInstance } from "src/api/Services/Tasks/tasks";
import { useGetSpaces } from "src/api/Spaces/spaces";
import { Badge } from "src/components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { getStatusSymbol } from "src/pages/Services/Tasks/Task";
import { clean, Filter } from "src/pages/spaces/space/projects";
import { getProjectMomentumFilterKey } from "src/utils/filters";
import { getTimeDifference } from "src/utils/functions/dateTime";

const baselineDateOptions: { id: string; label: string; value: string }[] = [
  {
    id: "7d",
    label: "1 week ago",
    value: dayjs().subtract(7, "days").toISOString(),
  },
  {
    id: "14d",
    label: "2 weeks ago",
    value: dayjs().subtract(14, "days").toISOString(),
  },
  {
    id: "28d",
    label: "4 weeks ago",
    value: dayjs().subtract(28, "days").toISOString(),
  },
];

export default function ProjectMomentumAnalytics() {
  const [filterGroup, setFilterGroup] = useState<ProjectsV2FilterGroup>(() => {
    const savedFilterGroup = localStorage.getItem(
      getProjectMomentumFilterKey()
    );
    if (savedFilterGroup) {
      return JSON.parse(savedFilterGroup);
    }
    return {
      logic: "AND",
      children: [],
    };
  });
  const [sort, setSort] = useState<ProjectProgressComparisonSort>({
    property: "incrementalClosedTasks",
    order: "asc",
  });

  useEffect(() => {
    if (filterGroup.children.length === 0) {
      localStorage.removeItem(getProjectMomentumFilterKey());
    } else {
      localStorage.setItem(
        getProjectMomentumFilterKey(),
        JSON.stringify(filterGroup)
      );
    }
  }, [filterGroup]);
  const { data: instanceMembers } = useGetTaskersForInstance();
  const { data: spaces } = useGetSpaces();
  const { journeyTemplates } = useGetJourneyTemplates();
  // const [baselineDate, setBaselineDate] = useState<Date>(
  //   dayjs().subtract(7, "days").toDate()
  // );
  const [selectedBaselineDate, setSelectedBaselineDate] = useState<{
    id: string;
    label: string;
    value: string;
  }>(baselineDateOptions[0]);

  const [totalNoOfPages, setTotalNoOfPages] = useState<number | undefined>(
    undefined
  );
  const [pageNo, setPageNo] = useState(1);

  useEffect(() => {
    setPageNo(1);
  }, [filterGroup]);

  // const [paginatedProjects, setPaginatedProjects] = useState<
  //   | {
  //       // noOfProjects: number;
  //       projects: ProjectProgressComparisonData[];
  //       pagination: {
  //         currentPage: number;
  //         pageSize: number;
  //         totalPages: number;
  //         totalRecords: number;
  //         baselineCount: number;
  //       };
  //     }
  //   | undefined
  // >(undefined);
  const { data: paginatedProjects, isLoading } =
    useGetProjectProgressComparison(
      {
        logic: "AND",
        children: [
          {
            field: "project-status",
            comparator: "in",
            value: ["In Progress"],
          },
        ],
      },
      clean(filterGroup),
      selectedBaselineDate.value,
      pageNo,
      sort
    );

  useEffect(() => {
    if (paginatedProjects?.pagination.totalPages === 0) {
      setTotalNoOfPages(1);
    } else if (paginatedProjects?.pagination.totalPages) {
      setTotalNoOfPages(paginatedProjects?.pagination.totalPages);
    }
  }, [paginatedProjects?.pagination.totalPages]);

  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8 text-sm text-gray-800">
        <div className="flex items-center space-x-2 text-sm">
          <NavLink to={`/analytics`}>
            <div className="text-gray-800">{"Analytics"}</div>
          </NavLink>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">
            {"Project Momentum"}
          </div>
          {/* <Button variant={"ghost"}>
            <InformationCircleIcon className="h-4 w-4 text-gray-500" />
          </Button> */}
        </div>
      </div>
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-gray-200/70 px-8">
        <div className="flex items-center gap-2">
          <Filter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            isLoading={!instanceMembers}
            filterModels={[
              {
                field: "project-owner",
                projectOwners:
                  instanceMembers?.map((m) => {
                    return {
                      _id: m._id,
                      name: m.name,
                      email: m.email,
                    };
                  }) ?? [],
              },
              {
                field: "space",
                spaces:
                  spaces?.map((s) => ({
                    _id: s._id,
                    name: s.name,
                    color: s.color,
                  })) ?? [],
              },
              {
                field: "project-template",
                projectTemplates:
                  journeyTemplates?.map((jt) => {
                    return {
                      _id: jt._id?.toString() ?? "",
                      name: jt.name,
                    };
                  }) ?? [],
              },
            ]}
          />
          <Select
            value={selectedBaselineDate.id}
            onValueChange={(value) => {
              setSelectedBaselineDate(
                baselineDateOptions.find((d) => d.id === value) ??
                  baselineDateOptions[0]
              );
            }}
          >
            <SelectTrigger
              className={`h-8 w-fit max-w-full gap-0 bg-white py-0 px-2 focus-visible:border-gray-300 focus-visible:ring-0`}
            >
              <div className="flex h-full items-center gap-2 divide-x">
                <div className="inline-flex items-center space-x-1.5">
                  <CalendarDaysIcon className="h-4 w-4 text-gray-600" />
                  <div className="">Baseline</div>
                </div>
                <div className="inline-flex h-full items-center pl-2">
                  {selectedBaselineDate.label}
                </div>
              </div>
            </SelectTrigger>
            <SelectContent>
              {baselineDateOptions.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* <div className="flex items-center text-sm">
            <div className="inline-flex h-8 items-center rounded-l-md border border-r-0 border-input px-2 pl-2.5">
              Sort by
            </div>
            <Select
              value={sort.property}
              onValueChange={(value) => {
                setSort({
                  property: value,
                  order: sort.order,
                } as ProjectProgressComparisonSort);
              }}
            >
              <SelectTrigger
                className={`h-8 w-fit max-w-full gap-0 rounded-none bg-white py-0 px-2 pl-2.5 focus-visible:border-gray-300 focus-visible:ring-0`}
              >
                <div className="inline-flex h-full items-center">
                  {sort.property === "assignedStartDate"
                    ? "days since start date"
                    : sort.property === "createdAt"
                    ? "days since creation date"
                    : sort.property === "incrementalClosedTasks"
                    ? "tasks closed since baseline"
                    : sort.property === "incrementalProjectProgress"
                    ? "project progress since baseline"
                    : sort.property === "incrementalTotalTasks"
                    ? "tasks created since baseline"
                    : sort.property === "incrementalMilestonesCompleted"
                    ? "milestones completed since baseline"
                    : sort.property === "projectProgress"
                    ? "project progress"
                    : sort.property === "milestonesCompleted"
                    ? "milestones completed"
                    : ""}
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Since Baseline</SelectLabel>
                  <SelectItem value="incrementalProjectProgress">
                    <div className="flex items-center space-x-1.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        className="h-3.5 w-3.5 text-gray-500"
                      >
                        <line x1="19" x2="5" y1="5" y2="19" />
                        <circle cx="6.5" cy="6.5" r="2.5" />
                        <circle cx="17.5" cy="17.5" r="2.5" />
                      </svg>
                      <div>project progress</div>
                    </div>
                  </SelectItem>
                  
                  <SelectItem value="incrementalClosedTasks">
                    <div className="flex items-center space-x-1.5">
                      <BoltIcon className="h-3.5 w-3.5 text-gray-500" />
                      <div>tasks closed</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="incrementalMilestonesCompleted">
                    <div className="flex items-center space-x-1.5">
                      <MilestoneIcon className="h-3.5 w-3.5 text-primary-main" />
                      <div>milestones completed</div>
                    </div>
                  </SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Current</SelectLabel>
                  <SelectItem value="projectProgress">
                    <div className="flex items-center space-x-1.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        className="h-3.5 w-3.5 text-gray-500"
                      >
                        <line x1="19" x2="5" y1="5" y2="19" />
                        <circle cx="6.5" cy="6.5" r="2.5" />
                        <circle cx="17.5" cy="17.5" r="2.5" />
                      </svg>
                      <div>project progress</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="milestonesCompleted">
                    <div className="flex items-center space-x-1.5">
                      <MilestoneIcon className="h-3.5 w-3.5 text-gray-500" />
                      <div>milestones completed</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="assignedStartDate">
                    <div className="flex items-center space-x-1.5">
                      <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-500" />
                      <div>days since start date</div>
                    </div>
                  </SelectItem>
                  <SelectItem value="createdAt">
                    <div className="flex items-center space-x-1.5">
                      <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-500" />
                      <div>days since creation date</div>
                    </div>
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <button
              className="inline-flex h-8 w-[62px] items-center justify-center gap-1 rounded-r-md border border-l-0 border-input hover:bg-gray-50"
              onClick={() => {
                setSort({
                  property: sort.property,
                  order: sort.order === "asc" ? "desc" : "asc",
                });
              }}
            >
              {sort.order === "asc" ? (
                <ArrowUpIcon className="-ml-1 h-3.5 w-3.5 text-gray-600" />
              ) : (
                <ArrowDownIcon className="-ml-1 h-3.5 w-3.5 text-gray-600" />
              )}
              <div>{sort.order === "asc" ? "asc" : "dec"}</div>
            </button>
          </div> */}

          <div
            className={clsx(
              "flex h-8 items-center space-x-2 overflow-hidden rounded-md border border-input shadow-sm"
              // isLoading && "opacity-50"
            )}
          >
            <div
              className={clsx(
                "flex h-full w-7 items-center justify-center border-r border-input transition-all",
                pageNo === 1
                  ? "cursor-not-allowed text-gray-300"
                  : "cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={() => {
                if (pageNo === 1) return;
                setPageNo((prev) => prev - 1);
              }}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </div>
            <div className="flex select-none items-center space-x-1 px-1 text-sm text-gray-600">
              <div className="min-w-[12px] text-center">{pageNo}</div>
              <SlashIcon className="h-3 w-3 text-gray-500" />
              <div className="min-w-[12px] text-center">
                {totalNoOfPages ? (
                  totalNoOfPages
                ) : (
                  <div className="h-3 w-3 animate-pulse rounded bg-gray-200" />
                )}
              </div>
            </div>
            <div
              className={clsx(
                "flex h-full w-7 items-center justify-center border-l border-input transition-all",
                pageNo === totalNoOfPages
                  ? "cursor-not-allowed text-gray-300"
                  : "cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={() => {
                if (pageNo === totalNoOfPages) return;
                setPageNo((prev) => prev + 1);
              }}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-auto">
        <ProjectsTable
          projects={paginatedProjects?.projects ?? []}
          baselineId={selectedBaselineDate.id}
          sort={sort}
          setSort={setSort}
        />
        {!!paginatedProjects ? (
          <>
            {paginatedProjects.projects.length > 0 ? (
              <></>
            ) : (
              <div className="mt-20 text-center text-gray-500">
                No projects found
              </div>
            )}
          </>
        ) : (
          <Loading className="mx-auto mt-[20vh]" />
        )}
      </div>
    </div>
  );
}

function getColumns(
  baselineId: string,
  sort: ProjectProgressComparisonSort,
  setSort: (sort: ProjectProgressComparisonSort) => void
): ColumnDef<ProjectProgressComparisonData>[] {
  return [
    {
      id: "project",
      header: () => {
        return <div className="flex w-80 items-center px-6 pl-8">Project</div>;
      },
      cell: ({ row }) => {
        return (
          <div className="flex h-10 w-80 items-center px-6 pl-8">
            <div className="truncate">{row.original.name}</div>
          </div>
        );
      },
    },
    {
      id: "since-baseline",
      header: () => {
        return (
          <div className="flex h-11 items-center justify-center bg-gray-100 px-6 text-gray-500">
            {baselineId === "7d"
              ? "In the last 1 week"
              : baselineId === "14d"
              ? "In the last 2 weeks"
              : baselineId === "30d"
              ? "In the last 4 weeks"
              : "Since Baseline"}
          </div>
        );
      },
      columns: [
        {
          id: "progress-since-baseline",
          header: ({ column }) => {
            return (
              <div className="flex h-11 w-44 items-center space-x-1 bg-gray-100 px-6 text-gray-500">
                <div>Progress Made</div>
                <NewSortToggle
                  isActive={sort.property === "incrementalProjectProgress"}
                  order={sort.order}
                  setSort={setSort}
                  property={"incrementalProjectProgress"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            // const currentProgress = row.original.projectProgress * 0.01;
            // const baselineProgress =
            //   row.original.baselineProgress.closedTasks /
            //   row.original.baselineProgress.totalTasks;
            // const incrementalProgress = currentProgress - baselineProgress;
            // const createdAtIsBeforeBaseline = dayjs(
            //   row.original.createdAt
            // ).isBefore(baselineDate);
            const incrementalProgress = row.original.incrementalProjectProgress;

            return (
              <>
                <div
                  className={clsx(
                    "w-44 px-6",
                    incrementalProgress > 0 ? "text-green-500" : "text-red-500"
                  )}
                >{`${Math.round(incrementalProgress)}%`}</div>
                {/* {createdAtIsBeforeBaseline ? (
                  
                  // <div
                  //   className={clsx(
                  //     "w-32",
                  //     incrementalProgress > 0
                  //       ? "text-green-500"
                  //       : "text-red-500"
                  //   )}
                  // >{`${
                  //   incrementalProgress ? Math.round(incrementalProgress) : 0
                  // }% ${currentProgress} ${baselineProgress} ${row.original.incrementalProjectProgress}`}</div>
                ) : (
                  <div className="w-32">-</div>
                )} */}
              </>
            );
          },
        },
        {
          id: "tasks-closed-since-baseline",
          header: ({ column }) => {
            return (
              <div className="flex h-11 w-48 items-center space-x-1 bg-gray-100 px-6 text-gray-500">
                <div>Tasks Completed</div>
                <NewSortToggle
                  isActive={sort.property === "incrementalClosedTasks"}
                  order={sort.order}
                  setSort={setSort}
                  property={"incrementalClosedTasks"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            const incrementalTasksClosed =
              row.original.closedTasks -
              row.original.baselineProgress.closedTasks;
            return (
              <div
                className={clsx(
                  "w-48 px-6",
                  incrementalTasksClosed > 0 ? "text-green-500" : "text-red-500"
                )}
              >
                {incrementalTasksClosed}
              </div>
            );
          },
        },
        // {
        //   id: "milestones-completed-since-baseline",
        //   header: ({ column }) => {
        //     return (
        //       <div className="flex h-11 w-52 items-center space-x-1 bg-gray-100 px-6 text-gray-500">
        //         <div>Milestones Completed</div>
        //       </div>
        //     );
        //   },
        //   cell: ({ row }) => {
        //     const currentMilestonesCompleted =
        //       row.original.targetProgress.milestones.filter(
        //         (m) => m.status === "Completed"
        //       ).length;
        //     const baselineMilestonesCompleted =
        //       row.original.baselineProgress.milestones.filter(
        //         (m) => m.status === "Completed"
        //       ).length;
        //     console.log(
        //       row.original.name,
        //       row.original.baselineProgress.milestones
        //     );
        //     const milestonesCompletedSinceBaseline =
        //       currentMilestonesCompleted - baselineMilestonesCompleted;
        //     return (
        //       <div className="w-52 px-6">
        //         {milestonesCompletedSinceBaseline}
        //       </div>
        //     );
        //   },
        // },
      ],
    },
    {
      id: "current",
      header: () => {
        return (
          <div className="flex items-center justify-center">As of today</div>
        );
      },
      columns: [
        {
          id: "progress",
          header: ({ column }) => {
            return (
              <div className="flex w-52 items-center space-x-1 px-6">
                <div>Progress</div>
                <NewSortToggle
                  isActive={sort.property === "projectProgress"}
                  order={sort.order}
                  setSort={setSort}
                  property={"projectProgress"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            const project = row.original;
            return (
              <div className="flex w-52 items-center space-x-3 px-6">
                <div
                  className={clsx(
                    "h-1.5 w-20 shrink-0 overflow-hidden rounded-sm",
                    Math.round(row.original.projectProgress) === 0
                      ? "border-gray-300 bg-gray-200/70"
                      : "border-green-200 bg-green-200/70"
                  )}
                >
                  <div
                    style={{
                      width: Math.round(row.original.projectProgress) + "%",
                    }}
                    className="h-full rounded-sm bg-green-500"
                  ></div>
                </div>
                <div className="text-sm">
                  {`${
                    project.projectProgress
                      ? Math.round(project.projectProgress)
                      : 0
                  }%`}
                </div>
              </div>
            );
          },
        },
        {
          id: "milestones",
          header: () => {
            return (
              <div className="flex w-40 items-center space-x-1 px-6">
                <div>Milestones</div>
              </div>
            );
          },
          cell: ({ row }) => {
            const milestones = row.original.targetProgress.milestones;
            return (
              <div className="relative flex w-40 items-center overflow-hidden px-6">
                <div className="pointer-events-none absolute right-3 h-10 w-11 bg-gradient-to-r from-transparent to-white" />
                <div className="pointer-events-none absolute right-0 h-10 w-3 bg-white" />
                <HoverCard openDelay={100} closeDelay={0}>
                  <HoverCardTrigger asChild>
                    <div className="flex h-8 w-full items-center space-x-1 rounded-md text-[13px] hover:bg-gray-50">
                      {milestones?.map((milestone) => (
                        <div key={milestone._id?.toString()}>
                          {getStatusSymbol(
                            milestone.status as ParentStatus,
                            false,
                            "small"
                          )}
                        </div>
                      ))}
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="max-w-xl"
                    side="right"
                    align="start"
                  >
                    <div className="space-y-2">
                      {milestones?.map((milestone) => (
                        <div
                          className="flex items-center justify-between space-x-4"
                          key={milestone._id?.toString()}
                        >
                          <div className="shrink-0">
                            {getStatusSymbol(
                              milestone.status as ParentStatus,
                              false,
                              "small"
                            )}
                          </div>
                          <div className="flex-1 text-sm">
                            {milestone.title}
                          </div>
                        </div>
                      ))}
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
              //     <HoverCard>
              //   <HoverCardTrigger>
              //   </HoverCardTrigger>
              //   <HoverCardContent>
              //     <div className="flex w-40">
              //       {milestones.map((m) => (
              //         <div key={m._id} className="flex items-center space-x-1">
              //           <div>{m.title}</div>
              //         </div>
              //       ))}
              //     </div>
              //   </HoverCardContent>
              // </HoverCard>
            );
          },
        },
        {
          id: "overdue-tasks",
          header: ({ column }) => {
            return (
              <div className="flex w-44 items-center space-x-1 px-6">
                <div>Tasks Overdue</div>
                <NewSortToggle
                  isActive={sort.property === "overdueTasks"}
                  order={sort.order}
                  setSort={setSort}
                  property={"overdueTasks"}
                  initialOrder={"desc"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            const overdueTasks = row.original.overdueTasks;
            return (
              <div
                className={clsx(
                  "flex w-44 items-center space-x-3 px-6",
                  overdueTasks === 0 && "text-gray-300"
                )}
              >
                {overdueTasks}
              </div>
            );
          },
        },
        {
          id: "stuck-tasks",
          header: ({ column }) => {
            return (
              <div className="flex w-44 items-center space-x-1 px-6">
                <div>Tasks Stuck</div>
                <NewSortToggle
                  isActive={sort.property === "stuckTasks"}
                  order={sort.order}
                  setSort={setSort}
                  property={"stuckTasks"}
                  initialOrder={"desc"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            const stuckTasks = row.original.stuckTasks;
            return (
              <div
                className={clsx(
                  "flex w-44 items-center space-x-3 px-6",
                  stuckTasks === 0 && "text-gray-300"
                )}
              >
                {stuckTasks}
              </div>
            );
          },
        },
      ],
    },
    {
      id: "owner",
      header: ({ column }) => {
        return (
          <div className="flex w-44 items-center space-x-1 px-6">
            <div>Owner</div>
            {/* <SortToggle column={column} /> */}
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="w-44 px-6">
            {row.original.owner ? (
              <div className="truncate">{row.original.owner.name}</div>
            ) : (
              <div className="text-gray-300">-</div>
            )}
          </div>
        );
      },
      accessorFn: ({ owner }) => owner.name,
    },
    {
      id: "last-update",
      header: () => {
        return (
          <div className="flex h-11 items-center justify-center bg-gray-100 px-6 text-gray-500">
            Last Project Update
          </div>
        );
      },
      columns: [
        {
          id: "last-update-status",
          header: ({ column }) => {
            return (
              <div className="flex h-11 w-40 items-center space-x-1 bg-gray-100 px-6">
                <div>Status</div>
                <NewSortToggle
                  isActive={sort.property === "lastInternalUpdateStatus"}
                  order={sort.order}
                  setSort={setSort}
                  property={"lastInternalUpdateStatus"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            const status = row.original.lastInternalUpdateStatus;
            return (
              <div className="w-40 px-6">
                {status === "On-Track" ? (
                  <Badge variant={"green"}>On-Track</Badge>
                ) : status === "At Risk" ? (
                  <Badge variant={"amber"}>At Risk</Badge>
                ) : status === "Off-Track" ? (
                  <Badge variant={"red"}>Off-Track</Badge>
                ) : (
                  <div className="text-gray-300">-</div>
                )}
              </div>
            );
          },
        },
        {
          id: "last-update-days-ago",
          header: ({ column }) => {
            return (
              <div className="flex h-11 w-40 items-center space-x-1 bg-gray-100 px-6">
                <div>Days Since</div>
                <NewSortToggle
                  isActive={sort.property === "lastInternalUpdateCreatedAt"}
                  order={sort.order}
                  setSort={setSort}
                  property={"lastInternalUpdateCreatedAt"}
                />
              </div>
            );
          },
          cell: ({ row }) => {
            const lastUpdateDateCreatedAt =
              row.original.lastInternalUpdateCreatedAt;
            if (!lastUpdateDateCreatedAt)
              return <div className="w-40 px-6 text-gray-300">-</div>;
            const daysSince = Math.ceil(dayjs().diff(lastUpdateDateCreatedAt, "days", true));
            return (
              <div className="w-40 px-6">
                <>{daysSince}</>
              </div>
            );
          },
        },
        {
          id: "last-update-content",
          header: ({ column }) => {
            return (
              <div className="flex h-11 w-72 items-center space-x-1 bg-gray-100 px-6">
                <div>Update</div>
                {/* <NewSortToggle
                  isActive={sort.property === "lastInternalUpdateStatus"}
                  order={sort.order}
                  setSort={setSort}
                  property={"lastInternalUpdateStatus"}
                /> */}
              </div>
            );
          },
          cell: ({ row }) => {
            const lastUpdate =
              row.original.pastInternalUpdates.length > 0
                ? row.original.pastInternalUpdates[0]
                : "";

            return (
              <>
                {lastUpdate && (
                  <LastUpdateContent
                    update={row.original.pastInternalUpdates[0]}
                  />
                )}
              </>
            );
          },
        },
      ],
    },
    {
      id: "days-since-start",
      header: ({ column }) => {
        return (
          <div className="flex w-52 items-center space-x-1 px-6">
            <div>Days Since Start</div>
            <NewSortToggle
              isActive={sort.property === "assignedStartDate"}
              order={sort.order}
              setSort={setSort}
              property={"assignedStartDate"}
              initialOrder="desc"
            />
          </div>
        );
      },
      cell: ({ row }) => {
        if (!row.original.assignedStartDate)
          return <div className="w-52 px-6 text-gray-300">-</div>;
        const daysActive = dayjs().diff(row.original.assignedStartDate, "days");
        return <div className="w-52 px-6">{daysActive}</div>;
      },
      // accessorFn: ({ createdAt }) => dayjs().diff(createdAt, "days"),
    },
    {
      id: "days-since-creation",
      header: ({ column }) => {
        return (
          <div className="flex w-52 items-center space-x-1 px-6">
            <div>Days Since Creation</div>
            <NewSortToggle
              isActive={sort.property === "createdAt"}
              order={sort.order}
              setSort={setSort}
              property={"createdAt"}
              initialOrder="desc"
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const daysActive = dayjs().diff(row.original.createdAt, "days");
        return <div className="w-52 px-6">{daysActive}</div>;
      },
      // accessorFn: ({ createdAt }) => dayjs().diff(createdAt, "days"),
    },
  ];
}

const extensions = [
  StarterKit,
  Link.configure({
    openOnClick: false,
    autolink: true,
  }),
  Typography,
  // Placeholder.configure({
  //   placeholder: "Write a project update...",
  //   showOnlyWhenEditable: false,
  // }),
];

function LastUpdateContent({ update }: { update: InternalPhaseUpdateData }) {
  const previewEditor = useEditor({
    extensions,
    content: update.description,
    editable: false,
  });
  const hoverEditor = useEditor({
    extensions,
    content: update.description,
    editable: false,
  });
  const { absValue, unit } = getTimeDifference(
    new Date(update.createdAt),
    new Date()
  );
  console.log(update);
  return (
    <HoverCard openDelay={100} closeDelay={0}>
      <HoverCardTrigger>
        <div className="relative h-11 w-72 overflow-hidden px-6 pt-2">
          <EditorContent editor={previewEditor} className="text-sm" />
          <div className="absolute bottom-0.5 left-0 right-0 h-2 bg-gradient-to-b from-transparent to-white/80" />
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-[480px] space-y-4"
        side="left"
        align="start"
      >
        <div className="flex h-6 w-fit items-center rounded-md bg-gray-100 px-2 text-xs font-light text-gray-500">
          <span>
            {absValue} {unit} ago
          </span>
          {!!update.createdBy && (
            <>
              <span className="whitespace-pre"> by </span>
              <span className="text-black"> {update.createdBy?.name}</span>
            </>
          )}
        </div>
        <EditorContent editor={hoverEditor} className="text-sm" />
      </HoverCardContent>
    </HoverCard>
  );
}

export function NewSortToggle({
  isActive,
  order,
  setSort,
  property,
  initialOrder,
}: {
  isActive: boolean;
  initialOrder?: "asc" | "desc";
  order: "asc" | "desc";
  setSort: (sort: ProjectProgressComparisonSort) => void;
  property: ProjectProgressComparisonSort["property"];
}) {
  return (
    <button
      className="inline-flex rounded-md px-1 py-1 hover:bg-gray-300/20"
      onClick={() => {
        if (!isActive && initialOrder) {
          setSort({
            property,
            order: initialOrder,
          });
          return;
        }

        if (isActive && order === "asc") {
          setSort({ property, order: "desc" });
        } else {
          setSort({ property, order: "asc" });
        }
      }}
    >
      <ArrowUpIcon
        className={clsx(
          "h-3 w-3 stroke-2 text-gray-300",
          isActive && order === "asc" && "text-primary-main"
        )}
      />
      <ArrowDownIcon
        className={clsx(
          "h-3 w-3 stroke-2 text-gray-300",
          isActive && order === "desc" && "text-primary-main"
        )}
      />
    </button>
  );
}

const ProjectsTable = ({
  projects,
  baselineId,
  sort,
  setSort,
}: {
  projects: ProjectProgressComparisonData[];
  baselineId: string;
  sort: ProjectProgressComparisonSort;
  setSort: (sort: ProjectProgressComparisonSort) => void;
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable<ProjectProgressComparisonData>({
    data: projects ?? [],
    columns: getColumns(baselineId, sort, setSort),
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      // globalFilter: search,
      // columnVisibility: transformColumnsToDisplay(columnsToDisplay),
    },
    // globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    // onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnsToDisplay,
  });
  const navigate = useNavigate();
  // console.log(projects);
  return (
    // <div className="flex-1 overflow-auto">
    <div className="max-h-full max-w-full overflow-auto">
      <table
        style={{ borderCollapse: "separate", borderSpacing: 0 }}
        className="min-w-[600px]"
      >
        <thead className="overflow-auto">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className={clsx(
                "z-20 border-b",
                headerGroup.depth === 0 && "sticky top-0",
                headerGroup.depth === 1 && "sticky top-[45px]"
              )}
            >
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    scope="col"
                    className={clsx(
                      "whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 p-0 text-xs font-normal uppercase tracking-wide text-gray-500 first:sticky first:left-0 first:z-30 last:w-full",
                      header.colSpan > 1 ? "text-center" : "text-left"
                      // header.depth === 0 ? "top-0" : "top-11"
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          <>
            <>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    navigate(`/projects/${row.original._id}`);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={
                        "whitespace-nowrap border-r border-b border-gray-100 p-0 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:w-full first:bg-white first:font-medium first:text-gray-900 first:group-hover:bg-gray-50"
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          </>
        </tbody>
      </table>
    </div>
    // </div>
  );
};

export function getCustomStatusSymbol(
  status: ParentStatus,
  isClickable: boolean = false
) {
  switch (status) {
    case "To-do":
      return (
        <div className="flex items-center space-x-2">
          {/* <div className={clsx("group rounded-md", isClickable && "p-0")}> */}
          <div
            className={clsx(
              "h-3 w-3 rounded-full border-[1.5px] border-amber-400 bg-amber-200 transition-all",
              isClickable && "hover:border-amber-500"
            )}
          />
          {/* </div> */}
          {/* <div className="h-3.5 w-3.5 rounded border border-amber-600 bg-amber-400" />
          <div className="h-3.5 w-3.5 rounded-full border border-amber-600 bg-amber-400" />
          <div className="h-3.5 w-3.5 rounded-full border-[1.5px] border-amber-400 bg-amber-100" /> */}
        </div>
      );
    case "Backlog":
      return (
        <div
          className={clsx(
            "h-3 w-3 rounded-full border-[1.5px] border-dashed border-gray-400 transition-all",
            isClickable && "hover:border-gray-500"
          )}
        />
      );

    case "Canceled":
      return (
        <div
          className={clsx(
            "flex h-3 w-3 items-center justify-center rounded-full bg-gray-500/60 text-white transition-all",
            isClickable && "hover:bg-gray-500/80"
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={3.5}
            stroke="currentColor"
            className="h-2 w-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </div>
      );

    case "In Progress":
      return (
        <div
          className={clsx(
            "group/status-in-progress flex h-3 w-3 items-center justify-center rounded-full border-[1.5px] border-blue-500 transition-all",
            isClickable && "hover:border-blue-600"
          )}
        >
          <div
            className={clsx(
              "h-1.5 w-1.5 rounded-full bg-blue-400",
              isClickable &&
                "transition-all group-hover/status-in-progress:bg-blue-500"
            )}
          />
        </div>
      );
    case "Stuck":
      return (
        <div
          className={clsx(
            "group/status-stuck flex h-3 w-3 items-center justify-center rounded-full border-[1.5px] border-red-500 transition-all",
            isClickable && "hover:border-red-600"
          )}
        >
          <div
            className={clsx(
              "h-1.5 w-1.5 rounded-full bg-red-400",
              isClickable &&
                "transition-all group-hover/status-stuck:bg-red-500"
            )}
          />
        </div>
      );
    case "Completed":
      return (
        <div className="flex items-center space-x-2">
          <div
            className={clsx(
              "flex h-3 w-3 items-center justify-center rounded-full bg-green-500 text-white transition-all",
              isClickable && "hover:bg-green-600"
            )}
          >
            {/* <svg
              width="14"
              height="14"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              strokeWidth={4}
            >
              <path
                d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
                fill="currentColor"
                fill-rule="evenodd"
                clip-rule="evenodd"
              ></path>
            </svg> */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={3}
              stroke="currentColor"
              className="h-2 w-2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
          </div>
          {/* <div className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded border-[1.5px] group border-green-600 hover:border-green-800 transition-all bg-green-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={3.5}
              stroke="currentColor"
              className="h-2.5 w-2.5 text-green-600 group-hover:text-green-800
              "
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
          </div>
          <div className="flex h-3.5 w-3.5 cursor-pointer items-center justify-center rounded  bg-green-500 hover:bg-green-600">
            
          </div> */}
          {/* <div className="h-3.5 w-3.5 rounded border border-amber-600 bg-amber-400" />
          <div className="h-3.5 w-3.5 rounded-full border border-amber-600 bg-amber-400" />
          <div className="h-3.5 w-3.5 rounded-full border-[1.5px] border-amber-400 bg-amber-100" /> */}
        </div>
      );
    default:
      return <div>TBD</div>;
  }
}
